import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, cn, Flex, Text, Icon } from '@kognitos/lattice';

import {
  BrowserPlayer,
  BrowserStepsViewer,
  BrowserTask,
  EBrowserPlayerState,
  EBrowserPreviewFabState,
  type IBrowserStepMarker,
  isRecordingUnavailableError,
  useBrowserReplay,
  useRecordingMetadata,
  useReplayVideo,
} from '@/modules/browser-automation';
import { formatPlaybackTime } from '@/modules/browser-automation/utils/format-playback-time';
import { isStepReached } from '@/modules/browser-automation/utils/step-offset';
import type { ScreencastState } from '@/shared/components/ScreencastViewer';
import { useTimeInterval } from '@/shared/hooks';
import { StatusDot } from '@/shared/components/StatusDot';
import {
  resolveAgentStreamUrl,
  resolveArtifactsBaseUrl,
} from '@/shared/utils/browser-urls';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { getBrowserLiveViewUrl } from '../../utils';
import { useReportBrowserPreviewStatus } from '../BrowserPreviewStatus';

import { type IBrowserRendererProps, ERenderMode } from '../../types';

/**
 * Fallback jump for the skip buttons when the recording has no steps to move
 * between — a zero-action session, or metadata that never resolved.
 */
const REPLAY_SKIP_SECONDS = 10;

/**
 * How close to a step boundary still counts as being *on* that step.
 *
 * Without it, pressing back a moment after landing on a step re-seeks to the
 * step you are already on, and the button appears dead until you have played
 * far enough past it.
 */
const STEP_SEEK_EPSILON_SECONDS = 0.35;

// Hover-revealed buttons: a mouse click must not leave focus behind, or the
// overlay stays pinned open until the user clicks elsewhere.
const preventFocusOnClick = (event: MouseEvent<HTMLButtonElement>) =>
  event.preventDefault();

// Touch screens have no hover to reveal the buttons, so they stay visible.
const HOVER_REVEAL_CLASS =
  'opacity-0 transition-opacity group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 [@media(hover:none)]:opacity-100';

/**
 * How long the replay loader waits for the video's first frame before giving
 * way to the player anyway. iOS Safari skips preloading, so `loadeddata` may
 * not fire until the user presses play.
 */
const REPLAY_READY_TIMEOUT_MS = 8_000;

/** How often the expanded live view re-reads metadata.json until it exists. */
const LIVE_METADATA_POLL_INTERVAL_MS = 5_000;

/**
 * Renders browser instance values across different display modes.
 *
 * - **Compact**: Shows a text label with the browser name and a Globe icon.
 * - **Preview**: Displays the BrowserPlayer with a live screencast (no chrome UI).
 * - **Expanded**: Two-panel layout with BrowserPlayer (with chrome UI) on the left
 *   and BrowserTask + BrowserStepsViewer on the right.
 *
 * @param props - Component props
 * @param props.value - Browser instance value with browser_name, live view URL and optional task
 * @param props.mode - Rendering mode (compact, preview, or expanded)
 * @param props.onExpandedClose - Optional callback when closing expanded view
 * @returns BrowserRenderer component
 */
export function BrowserRenderer({
  value,
  mode,
  label,
  forceSessionEnded,
  minimal = false,
  onExpand,
  onExpandedClose: _onExpandedClose,
}: IBrowserRendererProps) {
  'use no memo';
  // Run-paused signal comes from context (covers every browser surface at
  // once); an explicit prop still overrides it for standalone/test use.
  const { forceSessionEnded: contextForceSessionEnded } = useBrowserReplay();
  const effectiveForceSessionEnded =
    forceSessionEnded ?? contextForceSessionEnded;
  const { t } = useTranslation('browser-automation');
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const [screencastState, setScreencastState] =
    useState<ScreencastState | null>(null);
  const handleScreencastStateChange = useCallback((state: ScreencastState) => {
    setScreencastState(state);
  }, []);

  // Replay-mode plumbing for the shared steps sidebar:
  //  - videoRef: lets onStepClick imperatively seek the <video>
  //  - currentTime: drives the "active step" highlight in the sidebar
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const screenRef = useRef<HTMLDivElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Its own event, not piggybacked on onVideoTimeUpdate: that one doesn't
  // fire until playback/seek starts, leaving the total blank until then.
  const handleVideoDurationChange = useCallback((total: number) => {
    setDuration(total);
  }, []);

  const liveViewUrl = getBrowserLiveViewUrl(value);
  const agentStreamUrl =
    liveViewUrl && orgId && workspaceId
      ? resolveAgentStreamUrl(liveViewUrl, orgId, workspaceId)
      : null;
  const artifactsBase = useMemo(
    () => (liveViewUrl ? resolveArtifactsBaseUrl(liveViewUrl) : null),
    [liveViewUrl],
  );
  // `forceSessionEnded` covers the case where the browser pod is still alive
  // (live screencast never disconnects) but the run is paused for exception
  // guidance — we still want to fetch and show the replay-so-far. Without it,
  // replay only ever triggers on an actual screencast disconnect.
  const sessionEnded =
    effectiveForceSessionEnded ||
    screencastState === 'ended' ||
    screencastState === 'disconnected';

  // Replay video lifecycle (metadata fetch + poll + mp4 blob). Stays
  // idle until sessionEnded so the live screencast isn't slowed by a
  // background fetch during the active window. Returns `pending` until
  // the mp4 is loaded; `unavailable` for terminal failures (mount
  // missing, endpoint missing, poll-ceiling, zero-action, blob error).
  const replay = useReplayVideo(sessionEnded ? artifactsBase : null);

  // The step offsets the skip buttons move between. Same query key as
  // `useReplayVideo`'s own metadata fetch, so React Query serves both from one
  // request rather than doubling it. Expanded mode reads it while live too, for
  // the task description; BrowserLayout already subscribes there, so no extra fetch.
  const {
    data: recordingMetadata,
    error: recordingMetadataError,
    refetch: refetchRecordingMetadata,
  } = useRecordingMetadata(
    sessionEnded || mode === ERenderMode.EXPANDED ? artifactsBase : null,
  );
  // metadata.json only exists after the session's first action.
  const { start: startLiveMetadataPoll, stop: stopLiveMetadataPoll } =
    useTimeInterval(() => {
      void refetchRecordingMetadata();
    }, LIVE_METADATA_POLL_INTERVAL_MS);
  const valueTask = typeof value.task === 'string' ? value.task.trim() : '';
  const pollsLiveMetadata =
    !valueTask &&
    mode === ERenderMode.EXPANDED &&
    !sessionEnded &&
    artifactsBase !== null &&
    recordingMetadata == null &&
    !isRecordingUnavailableError(recordingMetadataError);
  // eslint-disable-next-line no-restricted-syntax -- reason: polling lifecycle with cleanup
  useEffect(() => {
    if (!pollsLiveMetadata) return;
    startLiveMetadataPoll();
    return stopLiveMetadataPoll;
  }, [pollsLiveMetadata, startLiveMetadataPoll, stopLiveMetadataPoll]);
  const stepOffsets = useMemo(() => {
    const steps = recordingMetadata?.steps ?? [];
    return steps
      .map(step => step.video_offset_seconds)
      .filter(offset => Number.isFinite(offset) && offset >= 0)
      .sort((a, b) => a - b);
  }, [recordingMetadata]);

  const browserLabel =
    (typeof value.browser_name === 'string' && value.browser_name) ||
    (typeof value.session_id === 'string' && value.session_id) ||
    'Browser';

  // Only swap to REPLAY when the recording is actually playable. While
  // the replay video is loading or unavailable, stay in LIVE — that
  // keeps ScreencastViewer mounted with its terminal state (last frame +
  // "session ended" badge), which is the fallback we want both during
  // the brief finalize window and for sessions that never produce a
  // recording (case 4 PVC-missing, zero-action sessions, etc.).
  // A recording that downloaded but won't play (bad codec, corrupt file)
  // falls back the same way. Keyed by URL so a new recording gets a fresh try.
  const [erroredVideoUrl, setErroredVideoUrl] = useState<string | null>(null);
  const videoErrored =
    replay.videoUrl !== null && erroredVideoUrl === replay.videoUrl;
  const handleVideoError = useCallback(() => {
    setErroredVideoUrl(replay.videoUrl);
  }, [replay.videoUrl]);

  const playerState =
    sessionEnded && liveViewUrl && replay.status === 'ready' && !videoErrored
      ? EBrowserPlayerState.REPLAY
      : EBrowserPlayerState.LIVE;

  // Steps sidebar swaps data source on sessionEnded, not on playerState. The
  // replay metadata can populate the sidebar even while the mp4 is still
  // loading — clicking a step before the video is ready just becomes a no-op
  // until videoRef binds, which is acceptable degraded UX.
  const isReplayMode = playerState === EBrowserPlayerState.REPLAY;

  // Keyed by URL so a new recording starts out not-ready without an effect.
  const [readyVideoUrl, setReadyVideoUrl] = useState<string | null>(null);
  const handleVideoReady = useCallback(() => {
    setReadyVideoUrl(replay.videoUrl);
  }, [replay.videoUrl]);
  const isVideoReady =
    isReplayMode &&
    replay.videoUrl !== null &&
    readyVideoUrl === replay.videoUrl;

  // eslint-disable-next-line no-restricted-syntax -- reason: timer lifecycle, so the loader can never outlive a video that never reports a frame
  useEffect(() => {
    if (!isReplayMode || isVideoReady) return;
    const timer = setTimeout(handleVideoReady, REPLAY_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isReplayMode, isVideoReady, handleVideoReady]);

  // One loader from session end until the video's first frame, instead of the
  // ended screencast followed by a blank <video> before the replay appears.
  const replayLoading =
    sessionEnded &&
    Boolean(liveViewUrl) &&
    (replay.status === 'pending' || (isReplayMode && !isVideoReady));

  // Playback controls. Only meaningful in replay: a live screencast is a frame
  // stream with nothing to seek, so the bar stays hidden until the mp4 is the
  // thing on screen.
  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  const seekBy = useCallback((deltaSeconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    // `duration` is NaN until metadata loads; clamping against NaN would make
    // every seek a no-op, so fall back to the lower bound only.
    const upperBound = Number.isFinite(video.duration)
      ? video.duration
      : Number.POSITIVE_INFINITY;
    const next = Math.min(
      upperBound,
      Math.max(0, video.currentTime + deltaSeconds),
    );
    video.currentTime = next;
    // Same reason as onStepClick below: `timeupdate` after a paused seek is
    // unreliable across browsers, so drive the label from the requested offset.
    setCurrentTime(next);
  }, []);

  // Step to step rather than by a fixed interval: the steps are what the
  // session is made of, and a ten-second hop lands between them — on a
  // four-minute recording that was two dozen presses to reach the end.
  const seekToAdjacentStep = useCallback(
    (direction: 1 | -1) => {
      const video = videoRef.current;
      if (!video) return;
      if (stepOffsets.length === 0) {
        seekBy(direction * REPLAY_SKIP_SECONDS);
        return;
      }
      const from = video.currentTime;
      const target =
        direction > 0
          ? stepOffsets.find(
              offset => offset > from + STEP_SEEK_EPSILON_SECONDS,
            )
          : [...stepOffsets]
              .reverse()
              .find(offset => offset < from - STEP_SEEK_EPSILON_SECONDS);
      const next =
        target ??
        (direction > 0
          ? Number.isFinite(video.duration)
            ? video.duration
            : from
          : 0);
      video.currentTime = next;
      // `timeupdate` after a paused seek is unreliable across browsers, so
      // drive the label from the requested offset.
      setCurrentTime(next);
    },
    [stepOffsets, seekBy],
  );

  const handleSkipBack = useCallback(
    () => seekToAdjacentStep(-1),
    [seekToAdjacentStep],
  );
  const handleSkipForward = useCallback(
    () => seekToAdjacentStep(1),
    [seekToAdjacentStep],
  );

  // Maximize means fullscreen on the video, or on the screen area while live
  // since no <video> exists yet. Only offered in expanded mode: the inline card
  // already has the OutputCard expand affordance.
  const handleMaximize = useCallback(() => {
    void (videoRef.current ?? screenRef.current)?.requestFullscreen?.();
  }, []);

  // Offsets come from executor frame counts and can overshoot the encoded
  // file's length slightly; clamp so trailing steps still get a marker.
  const stepMarkers = useMemo<IBrowserStepMarker[] | undefined>(() => {
    if (!(duration > 0) || !Number.isFinite(duration)) return undefined;
    return (recordingMetadata?.steps ?? [])
      .filter(
        step =>
          Number.isFinite(step.video_offset_seconds) &&
          step.video_offset_seconds >= 0,
      )
      .sort((a, b) => a.video_offset_seconds - b.video_offset_seconds)
      .map(step => {
        const seconds = Math.min(step.video_offset_seconds, duration);
        return {
          stepNumber: step.step_number,
          position: (seconds / duration) * 100,
          seconds,
          label: step.description,
          reached: isStepReached(seconds, currentTime),
        };
      });
  }, [recordingMetadata, duration, currentTime]);

  const seekTo = useCallback((seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
    }
    // `timeupdate` after a paused seek is unreliable across browsers, so
    // drive the label and step highlight from the requested offset.
    setCurrentTime(seconds);
  }, []);

  const handleStepMarkerClick = useCallback(
    (marker: IBrowserStepMarker) => seekTo(marker.seconds),
    [seekTo],
  );

  const getSessionStatusKey = () => {
    if (!sessionEnded) {
      return screencastState === 'connected'
        ? 'browserAutomation.task.live'
        : 'browserAutomation.task.connecting';
    }
    if (effectiveForceSessionEnded) return 'browserAutomation.task.paused';
    if (replay.status === 'unavailable' || videoErrored) {
      return 'browserAutomation.task.replayUnavailable';
    }
    return isVideoReady
      ? 'browserAutomation.task.replay'
      : 'browserAutomation.task.preparingReplay';
  };
  const taskDescription =
    valueTask ||
    recordingMetadata?.task_description?.trim() ||
    t(getSessionStatusKey());

  // Rendered from the first live frame, not once a recording exists — avoids
  // the layout shift of controls popping in as "Session ended" clears.
  const playbackControls = {
    fabState: EBrowserPreviewFabState.REPLAY,
    disabled: !isVideoReady,
    // Separate from `disabled`: the screencast can still be connecting while
    // the bar is already disabled, deciding spinner vs. "Live".
    connected: screencastState === 'connected',
    isPlaying,
    timestamp: formatPlaybackTime(currentTime),
    // undefined, not "0:00": there's no recording yet while live.
    duration: duration > 0 ? formatPlaybackTime(duration) : undefined,
    progress: duration > 0 ? (currentTime / duration) * 100 : 0,
    stepMarkers,
    onStepMarkerClick: handleStepMarkerClick,
    onPlayPause: handlePlayPause,
    onSkipBack: handleSkipBack,
    onSkipForward: handleSkipForward,
  };

  const isLivePreview = !sessionEnded && screencastState === 'connected';
  const hostShowsStatus = useReportBrowserPreviewStatus(
    (mode === ERenderMode.PREVIEW && minimal) || mode === ERenderMode.EXPANDED
      ? isLivePreview
        ? 'live'
        : isVideoReady
          ? 'replay'
          : null
      : null,
  );

  // Compact mode: simple text label. `label` overrides the browser identifier
  // (e.g. a fixed "Browser Operator" for the inline run-page pill).
  if (mode === ERenderMode.COMPACT) {
    return (
      <Flex gap={2} align="center">
        <Icon type="Globe" size="sm" />
        <Text level="small" color="muted" className="font-normal">
          {label ?? browserLabel}
        </Text>
      </Flex>
    );
  }

  if (mode === ERenderMode.PREVIEW && minimal) {
    return (
      <div
        className="group relative w-full overflow-hidden rounded-lg border border-border bg-background"
        data-testid="browser-minimal-preview"
      >
        <BrowserPlayer
          state={playerState}
          liveViewUrl={liveViewUrl}
          videoUrl={replay.videoUrl}
          readOnly
          frameless
          showControls={false}
          onScreencastStateChange={handleScreencastStateChange}
          videoRef={videoRef}
          onVideoTimeUpdate={setCurrentTime}
          onVideoDurationChange={handleVideoDurationChange}
          onVideoPlayStateChange={setIsPlaying}
          onVideoReady={handleVideoReady}
          onVideoError={handleVideoError}
          replayLoading={replayLoading}
        />
        {!hostShowsStatus && (isLivePreview || isVideoReady) && (
          <Flex
            align="center"
            gap={1.5}
            className="pointer-events-none absolute left-2 top-2 rounded-md bg-background/85 px-1.5 py-0.5 shadow-sm"
            data-testid="browser-minimal-preview-status"
          >
            {isLivePreview && <StatusDot tone="success" pulse />}
            <Text level="xSmall" weight="medium">
              {isLivePreview
                ? t('browserAutomation.action.live')
                : t('browserAutomation.video.replayBadge')}
            </Text>
          </Flex>
        )}
        {(isReplayMode || onExpand) && (
          <Flex
            align="center"
            justify="center"
            gap={2}
            className="pointer-events-none absolute inset-0 bg-foreground/0 transition-colors group-has-[:focus-visible]:bg-foreground/20 group-hover:bg-foreground/20"
          >
            {isReplayMode && (
              <Button
                type="button"
                variant="secondary"
                size="icon-sm"
                onMouseDown={preventFocusOnClick}
                onClick={event => {
                  // The host card opens the preview on click.
                  event.stopPropagation();
                  handlePlayPause();
                }}
                aria-label={
                  isPlaying
                    ? t('browserAutomation.action.pause')
                    : t('browserAutomation.action.play')
                }
                // Play stays up so a paused replay reads as playable; Pause
                // would only cover the video, so it waits for hover.
                className={cn(
                  'pointer-events-auto shadow-md',
                  isPlaying && HOVER_REVEAL_CLASS,
                )}
              >
                <Icon type={isPlaying ? 'Pause' : 'Play'} size="xs" />
              </Button>
            )}
            {onExpand && (
              <Button
                type="button"
                variant="secondary"
                size="icon-sm"
                onMouseDown={preventFocusOnClick}
                onClick={event => {
                  event.stopPropagation();
                  onExpand();
                }}
                aria-label={t('browserAutomation.action.expand')}
                className={cn(
                  'pointer-events-auto shadow-md',
                  HOVER_REVEAL_CLASS,
                )}
              >
                <Icon type="Maximize" size="xs" />
              </Button>
            )}
          </Flex>
        )}
      </div>
    );
  }

  // Preview mode: BrowserPlayer without chrome UI
  if (mode === ERenderMode.PREVIEW) {
    return (
      <BrowserPlayer
        state={playerState}
        liveViewUrl={liveViewUrl}
        videoUrl={replay.videoUrl}
        readOnly
        // Preview always renders inside an OutputCard, which already draws the
        // border, surface and padding. Without this the player drew a second
        // card inside the first.
        frameless
        // Always shown — see playbackControls' `disabled`. The card's own
        // expand button is the maximize affordance here, so none is passed.
        {...playbackControls}
        onScreencastStateChange={handleScreencastStateChange}
        videoRef={videoRef}
        onVideoTimeUpdate={setCurrentTime}
        onVideoDurationChange={handleVideoDurationChange}
        onVideoPlayStateChange={setIsPlaying}
        onVideoReady={handleVideoReady}
        onVideoError={handleVideoError}
        replayLoading={replayLoading}
      />
    );
  }

  // Expanded mode: two-panel layout with browser + task/steps.
  // The "Replay" badge rendered by BrowserLayout (inline with the header
  // title) handles the live-vs-replay affordance, so we don't render one
  // here.
  return (
    // Size container so the player can be capped by the height it has, not
    // just the width: a 16:9 frame sized by width alone overflows short screens.
    <Flex
      gap={4}
      className="h-full w-full [container-type:size]"
      align="start"
      justify="center"
    >
      {/* Left: Browser Player with chrome UI. 6rem covers the parts that don't
          scale with width: borders, the window bar and the controls bar. */}
      <div className="flex-[2] min-w-0 max-w-[calc((100cqh-6rem)*16/9)]">
        <BrowserPlayer
          state={playerState}
          liveViewUrl={liveViewUrl}
          videoUrl={replay.videoUrl}
          showChrome
          {...playbackControls}
          showLiveStatus={!hostShowsStatus}
          onMaximize={handleMaximize}
          onScreencastStateChange={handleScreencastStateChange}
          videoRef={videoRef}
          screenRef={screenRef}
          onVideoTimeUpdate={setCurrentTime}
          onVideoDurationChange={handleVideoDurationChange}
          onVideoPlayStateChange={setIsPlaying}
          onVideoReady={handleVideoReady}
          onVideoError={handleVideoError}
          replayLoading={replayLoading}
        />
      </div>

      {/* Right: Task description + step sidebar.
       *
       * Single component renders both:
       *   - Live session: streamUrl wins, useAgentStream opens an SSE.
       *   - Session ended: artifactsBase wins; useMetadataSteps fetches
       *     metadata.json once (deduped with useReplayVideo's fetch via
       *     React Query). Clicking a step seeks the video via videoRef
       *     once the mp4 is loaded.
       */}
      <Flex direction="column" gap={2} className="w-[320px] shrink-0 h-full">
        <BrowserTask
          title={t('browserAutomation.task.title')}
          description={taskDescription}
          className="w-full shrink-0"
        />
        {/* Own surface: the layover scrim is dark in both themes, so bare steps vanish in light mode. */}
        <Card className="w-full flex-1 min-h-0 gap-0 overflow-hidden p-2">
          <BrowserStepsViewer
            streamUrl={sessionEnded ? null : agentStreamUrl}
            artifactsBase={sessionEnded ? artifactsBase : null}
            sessionEnded={sessionEnded}
            className="w-full flex-1 min-h-0"
            activeStepTime={isReplayMode ? currentTime : undefined}
            onStepClick={
              sessionEnded
                ? step => {
                    if (step.video_offset_seconds == null) return;
                    seekTo(step.video_offset_seconds);
                  }
                : undefined
            }
          />
        </Card>
      </Flex>
    </Flex>
  );
}
